"use client";

import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useSearchParams } from "next/navigation";
import { useLocalStorageState } from "@/lib/hooks/useLocalStorageState";
import { extractUtmParams, type UtmParams } from "@/lib/utm/params";

export const UTM_STORAGE_KEY = "psycloud_utm";

interface UtmContextValue {
  utm: UtmParams | null;
  /** False until localStorage has been read. */
  hydrated: boolean;
}

const UtmContext = createContext<UtmContextValue | null>(null);

function UtmCapture({
  setUtm,
}: {
  setUtm: (value: UtmParams | null) => void;
}) {
  const searchParams = useSearchParams();

  useEffect(() => {
    const extracted = extractUtmParams(
      new URLSearchParams(searchParams.toString()),
    );
    if (extracted) setUtm(extracted);
  }, [searchParams, setUtm]);

  return null;
}

export function UtmProvider({ children }: { children: ReactNode }) {
  const [utm, setUtm, hydrated] = useLocalStorageState<UtmParams | null>(
    UTM_STORAGE_KEY,
    null,
  );

  const value = useMemo<UtmContextValue>(
    () => ({ utm, hydrated }),
    [utm, hydrated],
  );

  return (
    <UtmContext.Provider value={value}>
      <Suspense fallback={null}>
        <UtmCapture setUtm={setUtm} />
      </Suspense>
      {children}
    </UtmContext.Provider>
  );
}

export function useUtm(): UtmContextValue {
  const ctx = useContext(UtmContext);
  if (!ctx) throw new Error("useUtm must be used within <UtmProvider>");
  return ctx;
}
